// vim: tabstop=8 softtabstop=0 noexpandtab shiftwidth=8 nosmarttab

import { ThrottledFunction } from "throttle";
import { DesiredStateEventSource } from "./desired-state-event-source.js";

export abstract class BunnyBase {
	protected _cached_uri?: string | null;
	protected _cached_pull_interval?: number | null;
	protected _cached_push_interval?: number | null;
	protected _cached_min_backoff_interval?: number | null;
	protected _cached_max_backoff_interval?: number | null;
	protected _cached_desired_state_etag: string | null = null;
	protected _cached_desired_state_last_modified: string | null = null;
	protected _cached_runtime_state_etag: string | null = null;
	protected _cached_runtime_state_last_modified: string | null = null;
	protected _cached_runtime_status_etag: string | null = null;
	protected _cached_runtime_status_last_modified: string | null = null;
	protected _desired_state_event_source: DesiredStateEventSource | undefined = undefined;
	protected _throttle_desired_state: ThrottledFunction<() => Promise<void>> | undefined = undefined;
	protected _is_active: boolean = false;

	// Implement this in the derived class to provide a prefix for the storage keys.
	abstract get _storageKeyPrefix(): string;

	get isActive(): boolean {
		return this._is_active;
	}

	get desiredStateEventSource(): DesiredStateEventSource | undefined {
		return this._desired_state_event_source;
	}

	shutdown(): void {
		if(typeof this._desired_state_event_source !== 'undefined') {
			this._desired_state_event_source.shutdown();
			this._desired_state_event_source = undefined;
		}
	}

	// #region Desired State
	_readDesiredState(): boolean {
		let hasChanged = this._readUri();
		hasChanged = this._readPullInterval() || hasChanged;
		hasChanged = this._readPushInterval() || hasChanged;
		hasChanged = this._readMinBackoffInterval() || hasChanged;
		hasChanged = this._readMaxBackoffInterval() || hasChanged;
		return hasChanged;
	}

	async _writeDesiredState(
		desiredState: {
			uri: string | null,
			pull_interval: number | null,
			push_interval: number | null,
			min_backoff_interval: number | null,
			max_backoff_interval: number | null,
		},
		etag: string | null,
		lastModified: string | null
	): Promise<boolean> {
		console.log('writeUri', desiredState.uri);
		let hasChanged = await this._writeUri(desiredState.uri);
		console.log('writePullInterval', desiredState.pull_interval);
		hasChanged = this._writePullInterval(desiredState.pull_interval) || hasChanged;
		console.log('writePushInterval', desiredState.push_interval);
		hasChanged = this._writePushInterval(desiredState.push_interval) || hasChanged;
		hasChanged = this._writeMinBackoffInterval(desiredState.min_backoff_interval) || hasChanged;
		hasChanged = this._writeMaxBackoffInterval(desiredState.max_backoff_interval) || hasChanged;
		hasChanged = this._writeDesiredStateEtag(etag, lastModified) || hasChanged;
		console.log('hasChanged', hasChanged);
		return hasChanged;
	}

	async _deleteDesiredState(): Promise<void> {
		await this._writeUri(null);
		this._writePullInterval(null);
		this._writePushInterval(null);
		this._writeMinBackoffInterval(null);
		this._writeMaxBackoffInterval(null);
		this._writeDesiredStateEtag(null, null);
	}
	// #endregion

	// #region URI
	get uri(): string | null {
		return this._cached_uri || null;
	}

	_readUri(): boolean {
		const uri = this._readKeyString('uri');
		if(uri !== this._cached_uri) {
			this._cached_uri = uri;
			this._onUriChanged(uri);
			return true;
		}
		return false;
	}

	// Returns true if the value has changed.
	async _writeUri(uri: string | null): Promise<boolean> {
		if(this._cached_uri === uri) {
			return false;
		}
		this._cached_uri = uri;
		this._writeKeyString('uri', uri);
		await this._onUriChanged(uri);
		return true;
	}

	// Apply the URI change.
	abstract _onUriChanged(uri: string | null): Promise<void>;
	// #endregion

	// #region Pull Interval
	get pullInterval(): number | null {
		return this._cached_pull_interval || null;
	}

	_readPullInterval(): boolean {
		const pullInterval = this._readKeyInt('pullInterval');
		if(pullInterval !== this._cached_pull_interval) {
			this._cached_pull_interval = pullInterval;
			this._onPullIntervalChanged(pullInterval);
			return true;
		}
		return false;
	}

	// Returns true if the value has changed.
	_writePullInterval(pullInterval: number | null): boolean {
		if(this._cached_pull_interval === pullInterval) {
			return false;
		}
		this._cached_pull_interval = pullInterval;
		this._writeKeyInt('pullInterval', pullInterval);
		this._onPullIntervalChanged(pullInterval);
		return true;
	}

	abstract _onPullIntervalChanged(pullInterval: number | null): void;
	// #endregion

	// #region Push Interval
	get pushInterval(): number | null {
		return this._cached_push_interval || null;
	}

	_readPushInterval(): boolean {
		const pushInterval = this._readKeyInt('pushInterval');
		if(pushInterval !== this._cached_push_interval) {
			this._cached_push_interval = pushInterval;
			this._onPushIntervalChanged(pushInterval);
			return true;
		}
		return false;
	}

	// Returns true if the value has changed.
	_writePushInterval(pushInterval: number | null): boolean {
		if(this._cached_push_interval === pushInterval) {
			return false;
		}
		this._cached_push_interval = pushInterval;
		this._writeKeyInt('pushInterval', pushInterval);
		this._onPushIntervalChanged(pushInterval);
		return true;
	}

	abstract _onPushIntervalChanged(pushInterval: number | null): void;
	// #endregion

	// #region Min Backoff Interval
	get minBackoffInterval(): number | null {
		return this._cached_min_backoff_interval || null;
	}

	_readMinBackoffInterval(): boolean {
		const minBackoffInterval = this._readKeyInt('minBackoffInterval');
		if(minBackoffInterval !== this._cached_min_backoff_interval) {
			this._cached_min_backoff_interval = minBackoffInterval;
			this._onMinBackoffIntervalChanged(minBackoffInterval);
			return true;
		}
		return false;
	}

	// Returns true if the value has changed.
	_writeMinBackoffInterval(minBackoffInterval: number | null): boolean {
		if(this._cached_min_backoff_interval === minBackoffInterval) {
			return false;
		}
		this._cached_min_backoff_interval = minBackoffInterval;
		this._writeKeyInt('minBackoffInterval', minBackoffInterval);
		this._onMinBackoffIntervalChanged(minBackoffInterval);
		return true;
	}

	abstract _onMinBackoffIntervalChanged(minBackoffInterval: number | null): void;
	// #endregion

	// #region Max Backoff Interval
	get maxBackoffInterval(): number | null {
		return this._cached_max_backoff_interval || null;
	}

	_readMaxBackoffInterval(): boolean {
		const maxBackoffInterval = this._readKeyInt('maxBackoffInterval');
		if(maxBackoffInterval !== this._cached_max_backoff_interval) {
			this._cached_max_backoff_interval = maxBackoffInterval;
			this._onMaxBackoffIntervalChanged(maxBackoffInterval);
			return true;
		}
		return false;
	}

	// Returns true if the value has changed.
	_writeMaxBackoffInterval(maxBackoffInterval: number | null): boolean {
		if(this._cached_max_backoff_interval === maxBackoffInterval) {
			return false;
		}
		this._cached_max_backoff_interval = maxBackoffInterval;
		this._writeKeyInt('maxBackoffInterval', maxBackoffInterval);
		this._onMaxBackoffIntervalChanged(maxBackoffInterval);
		return true;
	}

	abstract _onMaxBackoffIntervalChanged(maxBackoffInterval: number | null): void;
	// #endregion

	// #region ETag and Last Modified
	get desiredStateEtag(): string | null {
		return this._cached_desired_state_etag || null;
	}
	get desiredStateLastModified(): string | null {
		return this._cached_desired_state_last_modified || null;
	}

	_writeDesiredStateEtag(
		etag: string | null,
		lastModified: string | null
	): boolean {
		if(this._cached_desired_state_etag === etag
			&& this._cached_desired_state_last_modified === lastModified)
		{
			return false;
		}
		this._cached_desired_state_etag = etag;
		this._cached_desired_state_last_modified = lastModified;
		return true;
	}
	// #endregion

	// #region Key String
	_readKeyString(key: string): string | null {
		const storageKey = `${this._storageKeyPrefix}.${key}`;
		const value = window.localStorage.getItem(storageKey);
		return value;
	}

	// `localStorage` does not support `null` values, however reading a
	// non-existent key returns `null`.  Therefore, items that are `null`
	// value will be removed.
	_writeKeyString(key: string, value: string | null): void {
		const storageKey = `${this._storageKeyPrefix}.${key}`;
		if(value === null) {
			window.localStorage.removeItem(storageKey);
			return;
		}
		window.localStorage.setItem(storageKey, value);
	}
	// #endregion

	// #region Key Int
	_readKeyInt(key: string): number | null {
		const value = this._readKeyString(key);
		// NaN must be escalated to prevent loops.
		return value ? parseInt(value, 10) : null;
	}

	_writeKeyInt(key: string, value: number | null): void {
		this._writeKeyString(key, value ? value.toString() : null);
	}
	// #endregion

	// #region Key Boolean
	// Default value for boolean is `false`.
	_readKeyBoolean(key: string): boolean {
		const value = this._readKeyInt(key);
		if(value === 1) {
			return true;
		}
		if(value === 0 || value === null) {
			return false;
		}
		throw new Error(`Invalid boolean value for key "${key}": ${value}`);
	}

	_writeKeyBoolean(key: string, value: boolean): void {
		this._writeKeyInt(key, value ? 1 : 0);
	}
	// #endregion

	_deleteKeyValue(key: string): void {
		this._writeKeyString(key, null);
	}
};
